import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  validateAlphabet,
  validatePhoneNumber,
  validateEmail,
  validateRequired,
  validateZipCode,
} from "../../utils/validation";
import { signUpApi } from "../../services/api";
import { AuthContext } from "../../context/AuthContext";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";

const SignUpForm = ({ onClose, access, phoneNumber, refresh }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "Mr.",
    name: "",
    firstName: "",
    lastName: "",
    phoneCode: "+91",
    phoneNumber: "",
    email: "",
    flat: "",
    wing: "",
    state: "",
    country: "",
    city: "",
    zipCode: "",
    building: "",
    street: "",
  });
  const { login } = useContext(AuthContext);
  const [errors, setErrors] = useState({});
  const [user_type, setUserType] = useState("resident");

  const [apiErrors, setApiErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const [result, setResult] = useState("");

  const inputRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    phoneNumber: useRef(null),
    email: useRef(null),
    flat: useRef(null),
    wing: useRef(null),
    state: useRef(null),
    country: useRef(null),
    city: useRef(null),
    zipCode: useRef(null),
    building: useRef(null),
    street: useRef(null),
  };

  useLockBodyScroll(true); // Lock only when visible

  useEffect(() => {
    console.log("Pre-filled phone number:", user_type);
    if (phoneNumber) {
      setFormData((prev) => ({
        ...prev,
        phoneNumber: phoneNumber,
      }));
    }
  }, [phoneNumber]);

  const handleSendQuery = async () => {
    setResult("Sending...");

    const payload = {
      access_key: "dd008842-7240-4294-b550-6b009b733248",
      from_name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      subject: "User Support Query from Create Account",
      message: `
        Name: ${formData.firstName} ${formData.lastName}
        Email: ${formData.email}
        Phone: ${formData.phoneCode}${formData.phoneNumber}
        Flat: ${formData.flat}
        Wing: ${formData.wing}
        Building: ${formData.building}
        Street: ${formData.street}
        City: ${formData.city}
        State: ${formData.state}
        Country: ${formData.country}
        Zip Code: ${formData.zipCode}
        Timestamp: ${new Date().toLocaleString()}
      `,
      botcheck: "",
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setResult("Query submitted successfully!");
        setTimeout(() => {
          setShowErrorPopup(false);
          setResult("");
        }, 2000);
      } else {
        setResult(data.message || "Failed to submit query");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setResult("Network error. Please try again.");
    }
  };

  const allowOnlyAlphabets = (value) => {
    return value
      .replace(/[^A-Za-z\s-]/g, "") // remove numbers & symbols
      .replace(/\s+/g, " ") // normalize spaces
      .trimStart(); // prevent leading space
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let cleanedValue = value;

    // 🔢 Numeric-only fields
    if (name === "flat" || name === "zipCode") {
      cleanedValue = value.replace(/\D/g, "");
    }

    // 🚫 Block spaces completely for first & last name
    if (name === "firstName" || name === "lastName") {
      cleanedValue = value.replace(/\s/g, "");
    }
    // 🔤 Alphabet-only fields
    if (
      name === "firstName" ||
      name === "lastName" ||
      name === "building" ||
      name === "street" ||
      name === "city" ||
      name === "state" ||
      name === "country" ||
      name === "wing"
    ) {
      cleanedValue = allowOnlyAlphabets(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      firstName: validateAlphabet(formData.firstName, "First name"),
      lastName: validateAlphabet(formData.lastName, "Last name"),
      phoneNumber: validatePhoneNumber(formData.phoneNumber),
      email: validateEmail(formData.email),
      flat: validateRequired(formData.flat, "Flat No."),
      wing: validateAlphabet(formData.wing, "Wing"),
      building: validateAlphabet(formData.building, "Building"),
      street: validateAlphabet(formData.street, "Area"),
      city: validateAlphabet(formData.city, "City"),
      state: validateAlphabet(formData.state, "State"),
      country: validateAlphabet(formData.country, "Country"),
      zipCode: validateZipCode(formData.zipCode),
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const payload = {
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email_id: formData.email,
          flat_no: parseInt(formData.flat),
          wing_name: formData.wing,
          building_name: formData.building,
          location: formData.street,
          pincode: parseInt(formData.zipCode),
        };

        const response = await signUpApi(payload);
        const { resident_data, member_data } = response.data;
        const userData = resident_data || member_data;

        login({ access, refresh }, userData, user_type);
        localStorage.setItem("firstTimeLogin", "true");
        navigate("/resident-parcel-dashboard", { replace: true });
      } catch (error) {
        console.error("Signup error:", error);
        setIsSubmitting(false);

        if (error.response?.status === 400) {
          const errorDetails = error.response?.data?.error_details;

          if (errorDetails) {
            const fieldMapping = {
              email_id: "email",
              flat_no: "flat",
              wing_name: "wing",
              building_name: "building",
              location: "street",
              pincode: "zipCode",
            };

            const apiErrors = {};

            Object.keys(errorDetails).forEach((apiField) => {
              const formField = fieldMapping[apiField] || apiField;
              apiErrors[formField] = errorDetails[apiField][0];
            });

            const firstErrorKey = Object.keys(errorDetails)[0];
            setApiErrorMessage(errorDetails[firstErrorKey][0]);
            setErrors(apiErrors);
            setShowErrorPopup(true);
            return;
          }
        }

        const fallbackMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Signup failed. Please try again.";

        setErrors({ form: fallbackMessage });
        setApiErrorMessage(fallbackMessage);
        setShowErrorPopup(true);
      }
    }
  };

  const renderError = (fieldName) => {
    const fieldError = errors[fieldName];
    const apiFieldError = apiErrors[fieldName];

    if (fieldError || apiFieldError) {
      return (
        <p className="mt-1 text-red-500 text-xs font-medium">
          {fieldError || apiFieldError}
        </p>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
      <div
        ref={modalRef}
        className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto bg-white relative rounded-2xl p-6 shadow-xl"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl z-10"
        >
          ✕
        </button>

        {/* Title */}
        <div className="text-center mt-2 mb-6">
          <h1 className="text-[#312E2D] font-semibold text-xl uppercase">
            Create Account
          </h1>
          <p className="text-xs text-gray-500 mt-2">
            Fields marked with <span className="text-red-500 font-bold">*</span>{" "}
            are mandatory
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.form && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-medium">
              {errors.form}
            </div>
          )}

          {/* Title + First Name + Last Name */}
          <div className="flex gap-2">
            <select
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-[30%] p-3 border border-[#D9D9D9] rounded-xl text-sm"
            >
              <option value="Mr.">Mr.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Ms.">Ms.</option>
              <option value="Dr.">Dr.</option>
            </select>
            <div className="w-[35%]">
              <input
                type="text"
                name="firstName"
                placeholder="First Name *"
                ref={inputRefs.firstName}
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.firstName ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("firstName")}
            </div>
            <div className="w-[35%]">
              <input
                type="text"
                name="lastName"
                placeholder="Last Name *"
                ref={inputRefs.lastName}
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.lastName ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("lastName")}
            </div>
          </div>

          {/* Phone */}
          <div className="flex gap-2">
            <input
              type="tel"
              name="phoneCode"
              value={formData.phoneCode}
              readOnly
              className="w-[25%] p-3 border border-[#D9D9D9] rounded-xl bg-gray-100 text-sm"
            />
            <div className="w-[75%]">
              <input
                type="tel"
                name="phoneNumber"
                placeholder="Phone Number *"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.phoneNumber ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
                disabled
              />
              {renderError("phoneNumber")}
            </div>
          </div>

          {/* Email */}
          <div>
            <input
              type="email"
              name="email"
              placeholder="Email ID *"
              value={formData.email}
              ref={inputRefs.email}
              onChange={handleChange}
              className={`w-full p-3 border ${
                errors.email ? "border-red-500" : "border-[#D9D9D9]"
              } rounded-xl text-sm`}
            />
            {renderError("email")}
          </div>

          {/* Building */}
          <div>
            <input
              type="text"
              name="building"
              placeholder="Building / Apartment Name *"
              value={formData.building}
              ref={inputRefs.building}
              onChange={handleChange}
              className={`w-full p-3 border ${
                errors.building ? "border-red-500" : "border-[#D9D9D9]"
              } rounded-xl text-sm`}
            />
            {renderError("building")}
          </div>

          {/* Flat + Wing */}
          <div className="flex gap-2">
            <div className="w-1/2">
              <input
                type="text"
                name="wing"
                placeholder="Wing *"
                value={formData.wing}
                ref={inputRefs.wing}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.wing ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("wing")}
            </div>
            <div className="w-1/2">
              <input
                type="text"
                name="flat"
                placeholder="Flat No. *"
                maxLength={5}
                inputMode="numeric"
                value={formData.flat}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.flat ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("flat")}
            </div>
          </div>

          {/* Street */}
          <div>
            <input
              type="text"
              name="street"
              placeholder="Area (ex: Dahisar East) *"
              value={formData.street}
              ref={inputRefs.street}
              onChange={handleChange}
              className={`w-full p-3 border ${
                errors.street ? "border-red-500" : "border-[#D9D9D9]"
              } rounded-xl text-sm`}
            />
            {renderError("street")}
          </div>

          {/* Country + Zip */}
          <div className="flex gap-2">
            <div className="w-1/2">
              <input
                type="text"
                name="country"
                placeholder="Country *"
                value={formData.country}
                ref={inputRefs.country}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.country ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("country")}
            </div>
            <div className="w-1/2">
              <input
                type="tel"
                inputMode="numeric"
                name="zipCode"
                placeholder="Zip Code *"
                value={formData.zipCode}
                ref={inputRefs.zipCode}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.zipCode ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("zipCode")}
            </div>
          </div>

          {/* City + State */}
          <div className="flex gap-2">
            <div className="w-1/2">
              <input
                type="text"
                name="city"
                placeholder="City *"
                value={formData.city}
                ref={inputRefs.city}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.city ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("city")}
            </div>
            <div className="w-1/2">
              <input
                type="text"
                name="state"
                placeholder="State *"
                value={formData.state}
                ref={inputRefs.state}
                onChange={handleChange}
                className={`w-full p-3 border ${
                  errors.state ? "border-red-500" : "border-[#D9D9D9]"
                } rounded-xl text-sm`}
              />
              {renderError("state")}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full h-[50px] bg-[#004990] text-white text-[16px] font-semibold rounded-xl hover:bg-[#003366] transition ${
              isSubmitting ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "Processing..." : "Create Account"}
          </button>
        </form>
      </div>

      {showErrorPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] max-w-md text-center shadow-xl">
            <h2 className="text-lg font-semibold text-red-600 mb-4">
              User Not Found
            </h2>
            <p className="mb-6 text-red-500 font-medium">{apiErrorMessage}</p>

            {result && (
              <p
                className={`mb-4 font-medium ${
                  result.includes("successfully")
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {result}
              </p>
            )}

            <div className="flex justify-center gap-4">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium"
                onClick={() => {
                  setShowErrorPopup(false);

                  const errorFields = Object.keys(errors);
                  if (errorFields.length > 0) {
                    const firstErrorField = errorFields[0];
                    const inputRef = inputRefs[firstErrorField]?.current;

                    if (inputRef) {
                      inputRef.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });

                      setTimeout(() => {
                        inputRef.focus();
                      }, 300);
                    }
                  }
                }}
              >
                Stay Here
              </button>
              <button
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 font-medium"
                onClick={handleSendQuery}
              >
                {result.startsWith("Sending") ? "Sending..." : "Send Query"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUpForm;
